// src/components/auth/Login.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle, Phone, Smartphone, User, AtSign, Hash, X as XIcon } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useHolidays } from '../../contexts/HolidayContext';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useLeave } from '../../contexts/LeaveContext';
import { usePayroll } from '../../contexts/PayrollContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import Alert from '../common/Alert';
import OakTreeLoader from '../common/OakTreeLoader';
import authService from '../../services/auth.service';
import FeatureShowcase from './FeatureShowcase';
// import logo from '../../oakhr-logo-full.svg'; // Using video logo instead
import logoVideo from '../../logo.mp4';

const Login = () => {
    const navigate = useNavigate();
    const { login, sendOTP, verifyOTP, resendOTP } = useAuth();
    const { fetchHolidays } = useHolidays();
    const { fetchEmployees } = useEmployees();
    const { fetchAttendance } = useAttendance();
    const { fetchLeaveData } = useLeave();
    const { fetchPayrollData } = usePayroll();
    const { fetchOnboardingData, fetchChecklistConfig } = useOnboarding();

    // Login method: 'email' or 'mobile'
    const [loginMethod, setLoginMethod] = useState('email');

    // Email login form data
    const [formData, setFormData] = useState({
        companyDomain: '',
        email: '',
        password: ''
    });

    // Mobile OTP form data
    const [mobileData, setMobileData] = useState({
        companyDomain: '',
        phone: '',
        otp: ['', '', '', '', '', '']
    });

    // OTP state
    const [otpSent, setOtpSent] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);
    const [demoOTPHint, setDemoOTPHint] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showLoader, setShowLoader] = useState(false);
    const [redirectPath, setRedirectPath] = useState(null);
    const [showOrgModal, setShowOrgModal] = useState(false);
    const [orgStep, setOrgStep] = useState('form'); // 'form' | 'password' | 'success'
    const [orgForm, setOrgForm] = useState({
        companyName: '',
        domain: '',
        workEmail: '',
        emailVerified: false,
        phone: '',
        phoneVerified: false,
        firstName: '',
        middleName: '',
        lastName: '',
        teamSize: '',
        password: '',
        confirmPassword: ''
    });

    // Verification states
    const [emailOTP, setEmailOTP] = useState(['', '', '', '', '', '']);
    const [phoneOTP, setPhoneOTP] = useState(['', '', '', '', '', '']);
    const [emailOTPSent, setEmailOTPSent] = useState(false);
    const [phoneOTPSent, setPhoneOTPSent] = useState(false);
    const [emailOTPTimer, setEmailOTPTimer] = useState(0);
    const [phoneOTPTimer, setPhoneOTPTimer] = useState(0);
    const [emailVerifying, setEmailVerifying] = useState(false);
    const [phoneVerifying, setPhoneVerifying] = useState(false);

    // Duplicate check states
    const [emailDuplicate, setEmailDuplicate] = useState(null);
    const [phoneDuplicate, setPhoneDuplicate] = useState(null);
    const [orgDomainError, setOrgDomainError] = useState(null);
    const [checkingDuplicate, setCheckingDuplicate] = useState(false);

    // OTP input refs for org form
    const emailOTPRefs = useRef([]);
    const phoneOTPRefs = useRef([]);

    // Forgot password modal state
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [forgotPasswordDomain, setForgotPasswordDomain] = useState('');
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
    const [forgotPasswordError, setForgotPasswordError] = useState('');

    // OTP input refs
    const otpRefs = useRef([]);

    // Field-level error states
    const [fieldErrors, setFieldErrors] = useState({
        companyDomain: '',
        email: '',
        password: '',
        phone: '',
        otp: ''
    });
    const [touchedFields, setTouchedFields] = useState({
        companyDomain: false,
        email: false,
        password: false,
        phone: false
    });

    // OTP countdown timer
    useEffect(() => {
        let interval;
        if (otpTimer > 0) {
            interval = setInterval(() => {
                setOtpTimer(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [otpTimer]);

    // Email OTP countdown timer
    useEffect(() => {
        let interval;
        if (emailOTPTimer > 0) {
            interval = setInterval(() => {
                setEmailOTPTimer(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [emailOTPTimer]);

    // Phone OTP countdown timer
    useEffect(() => {
        let interval;
        if (phoneOTPTimer > 0) {
            interval = setInterval(() => {
                setPhoneOTPTimer(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [phoneOTPTimer]);

    const handleOrgFieldChange = (field, value) => {
        setOrgForm(prev => ({ ...prev, [field]: value }));

        // Reset duplicate check and verification when email or phone changes
        if (field === 'workEmail') {
            setEmailDuplicate(null);
            setOrgForm(prev => ({ ...prev, emailVerified: false }));
            setEmailOTPSent(false);
            setEmailOTP(['', '', '', '', '', '']);
        }
        if (field === 'phone') {
            setPhoneDuplicate(null);
            setOrgForm(prev => ({ ...prev, phoneVerified: false }));
            setPhoneOTPSent(false);
            setPhoneOTP(['', '', '', '', '', '']);
        }
    };

    // Check for duplicate email
    const checkEmailDuplicate = async (email) => {
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
        setCheckingDuplicate(true);
        try {
            const result = await authService.checkEmailDuplicate(email);
            if (result.exists) {
                setEmailDuplicate('This email is already registered with another organization');
            }
        } catch (error) {
            console.error('Email duplicate check error:', error);
        } finally {
            setCheckingDuplicate(false);
        }
    };

    // Check for duplicate phone
    const checkPhoneDuplicate = async (phone) => {
        if (!phone || phone.length < 10) return;
        setCheckingDuplicate(true);
        try {
            const result = await authService.checkPhoneDuplicate(phone);
            if (result.exists) {
                setPhoneDuplicate('This phone number is already registered');
            }
        } catch (error) {
            console.error('Phone duplicate check error:', error);
        } finally {
            setCheckingDuplicate(false);
        }
    };

    // Send Email OTP
    const handleSendEmailOTP = async () => {
        if (!orgForm.workEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orgForm.workEmail)) {
            setEmailDuplicate('Please enter a valid email');
            return;
        }

        setEmailVerifying(true);
        try {
            const result = await authService.sendEmailOTP(orgForm.workEmail);
            setEmailOTPSent(true);
            setEmailOTPTimer(30);
            setTimeout(() => emailOTPRefs.current[0]?.focus(), 100);

            // Show demo OTP hint if available
            if (result.hint) {
                setEmailDuplicate(result.hint);
            }
        } catch (error) {
            setEmailDuplicate(error.message || 'Failed to send OTP');
        } finally {
            setEmailVerifying(false);
        }
    };

    // Verify Email OTP
    const handleVerifyEmailOTP = async () => {
        const otpValue = emailOTP.join('');
        if (otpValue.length !== 6) return;

        setEmailVerifying(true);
        try {
            await authService.verifyEmailOTP(orgForm.workEmail, otpValue);
            setOrgForm(prev => ({ ...prev, emailVerified: true }));
            setEmailOTPSent(false);
            setEmailOTP(['', '', '', '', '', '']);
            setEmailDuplicate(null);
        } catch (error) {
            setEmailDuplicate(error.message || 'Invalid OTP. Please try again.');
        } finally {
            setEmailVerifying(false);
        }
    };

    // Send Phone OTP
    const handleSendPhoneOTP = async () => {
        const phone = orgForm.phone.replace(/\D/g, '');
        if (!phone || phone.length !== 10 || !/^[6-9]\d{9}$/.test(phone)) {
            setPhoneDuplicate('Please enter a valid 10-digit mobile number');
            return;
        }

        setPhoneVerifying(true);
        try {
            const result = await authService.sendPhoneOTP(orgForm.phone);
            setPhoneOTPSent(true);
            setPhoneOTPTimer(30);
            setTimeout(() => phoneOTPRefs.current[0]?.focus(), 100);

            // Show demo OTP hint if available
            if (result.hint) {
                setPhoneDuplicate(result.hint);
            }
        } catch (error) {
            setPhoneDuplicate(error.message || 'Failed to send OTP');
        } finally {
            setPhoneVerifying(false);
        }
    };

    // Verify Phone OTP
    const handleVerifyPhoneOTP = async () => {
        const otpValue = phoneOTP.join('');
        if (otpValue.length !== 6) return;

        setPhoneVerifying(true);
        try {
            await authService.verifyPhoneOTP(orgForm.phone, otpValue);
            setOrgForm(prev => ({ ...prev, phoneVerified: true }));
            setPhoneOTPSent(false);
            setPhoneOTP(['', '', '', '', '', '']);
            setPhoneDuplicate(null);
        } catch (error) {
            setPhoneDuplicate(error.message || 'Invalid OTP. Please try again.');
        } finally {
            setPhoneVerifying(false);
        }
    };

    // Handle Email OTP change
    const handleEmailOTPChange = (index, value) => {
        if (value && !/^\d$/.test(value)) return;
        const newOTP = [...emailOTP];
        newOTP[index] = value;
        setEmailOTP(newOTP);
        if (value && index < 5) emailOTPRefs.current[index + 1]?.focus();
    };

    // Handle Phone OTP change
    const handlePhoneOTPChange = (index, value) => {
        if (value && !/^\d$/.test(value)) return;
        const newOTP = [...phoneOTP];
        newOTP[index] = value;
        setPhoneOTP(newOTP);
        if (value && index < 5) phoneOTPRefs.current[index + 1]?.focus();
    };

    const handleOrgSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!orgForm.firstName.trim()) {
            setError('Please enter your first name.');
            return;
        }
        if (!orgForm.lastName.trim()) {
            setError('Please enter your last name.');
            return;
        }
        if (!orgForm.companyName.trim()) {
            setError('Please enter company name.');
            return;
        }
        if (!orgForm.domain || !orgForm.domain.trim()) {
            setError('Please enter an organization domain.');
            return;
        }
        if (!/^[a-z0-9-]+$/.test(orgForm.domain)) {
            setError('Domain can only contain lowercase letters, numbers, and hyphens.');
            return;
        }
        if (!orgForm.workEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orgForm.workEmail)) {
            setError('Please enter a valid work email.');
            return;
        }
        if (!orgForm.emailVerified) {
            setError('Please verify your email address.');
            return;
        }
        if (!orgForm.phone || orgForm.phone.replace(/\D/g, '').length !== 10) {
            setError('Please enter a valid phone number.');
            return;
        }

        // Submit to backend and move to password step
        setEmailVerifying(true);

        try {
            const result = await authService.registerOrganization(orgForm);
            // Store org data for password step
            sessionStorage.setItem('pendingOrgRegistration', JSON.stringify({
                ...orgForm,
                registrationResult: result
            }));
            setOrgStep('password');
        } catch (error) {
            setError(error.message || 'Registration failed. Please try again.');
        } finally {
            setEmailVerifying(false);
        }
    };

    const handlePasswordSetup = async (e) => {
        e.preventDefault();
        setError('');

        const { password, confirmPassword } = orgForm;

        if (!password || password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setError('');

        try {
            // Get pending registration data
            const pendingData = sessionStorage.getItem('pendingOrgRegistration');
            if (!pendingData) {
                throw new Error('Registration data not found. Please start over.');
            }

            const registrationData = JSON.parse(pendingData);

            // Create user account and login
            const loginData = await authService.completeOrganizationRegistration({
                ...registrationData,
                password: password,
                accessLevel: 'admin',
                secondaryAccess: 'employee'
            });

            console.log('✅ Registration successful:', loginData);

            // Store auth data
            localStorage.setItem('authToken', loginData.token);
            localStorage.setItem('user', JSON.stringify(loginData.user));
            localStorage.setItem('company', JSON.stringify(loginData.company));

            // Clear pending data
            sessionStorage.removeItem('pendingOrgRegistration');
            sessionStorage.removeItem('pendingOrgRegistration');

            // Clear org modal state
            setShowOrgModal(false);
            setOrgStep('form');

            console.log('🔄 Redirecting to /admin/dashboard...');

            // Direct navigation without loader - more reliable
            window.location.href = '/admin/dashboard';
        } catch (error) {
            console.error('Password setup failed:', error);
            setError(error.message || 'Failed to create account. Please try again.');
        }
    };

    const handleBackToForm = () => {
        setOrgStep('form');
        setError('');
    };

    // Validation functions
    const validateField = (name, value) => {
        let error = '';

        switch (name) {
            case 'companyDomain':
                if (!value.trim()) {
                    error = 'Company domain is required';
                } else if (value.length < 2) {
                    error = 'Company domain must be at least 2 characters';
                } else if (!/^[a-zA-Z0-9-]+$/.test(value)) {
                    error = 'Company domain can only contain letters, numbers, and hyphens';
                }
                break;

            case 'email':
                if (!value.trim()) {
                    error = 'Email address is required';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error = 'Please enter a valid email address';
                }
                break;

            case 'password':
                if (!value) {
                    error = 'Password is required';
                } else if (value.length < 6) {
                    error = 'Password must be at least 6 characters';
                }
                break;

            case 'phone':
                if (!value.trim()) {
                    error = 'Phone number is required';
                } else if (!/^[6-9]\d{9}$/.test(value.replace(/\D/g, ''))) {
                    error = 'Please enter a valid 10-digit Indian mobile number';
                }
                break;

            default:
                break;
        }

        return error;
    };

    const validateEmailForm = () => {
        const errors = {};
        let isValid = true;

        ['companyDomain', 'email', 'password'].forEach(field => {
            const error = validateField(field, formData[field]);
            if (error) {
                errors[field] = error;
                isValid = false;
            }
        });

        setFieldErrors(prev => ({ ...prev, ...errors }));
        setTouchedFields(prev => ({
            ...prev,
            companyDomain: true,
            email: true,
            password: true
        }));

        return isValid;
    };

    const validateMobileForm = (checkOTP = false) => {
        const errors = {};
        let isValid = true;

        // Validate company domain
        const domainError = validateField('companyDomain', mobileData.companyDomain);
        if (domainError) {
            errors.companyDomain = domainError;
            isValid = false;
        }

        // Validate phone
        const phoneError = validateField('phone', mobileData.phone);
        if (phoneError) {
            errors.phone = phoneError;
            isValid = false;
        }

        // Validate OTP if checking
        if (checkOTP) {
            const otpValue = mobileData.otp.join('');
            if (otpValue.length !== 6) {
                errors.otp = 'Please enter the complete 6-digit OTP';
                isValid = false;
            }
        }

        setFieldErrors(prev => ({ ...prev, ...errors }));
        setTouchedFields(prev => ({
            ...prev,
            companyDomain: true,
            phone: true
        }));

        return isValid;
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (error) {
            setError('');
        }

        if (touchedFields[name]) {
            const fieldError = validateField(name, value);
            setFieldErrors(prev => ({
                ...prev,
                [name]: fieldError
            }));
        }
    };

    const handleMobileFieldChange = (e) => {
        const { name, value } = e.target;

        setMobileData(prev => ({
            ...prev,
            [name]: value
        }));

        if (error) {
            setError('');
        }

        if (touchedFields[name]) {
            const fieldError = validateField(name, value);
            setFieldErrors(prev => ({
                ...prev,
                [name]: fieldError
            }));
        }
    };

    const handleOTPChange = (index, value) => {
        // Only allow digits
        if (value && !/^\d$/.test(value)) return;

        const newOTP = [...mobileData.otp];
        newOTP[index] = value;
        setMobileData(prev => ({ ...prev, otp: newOTP }));

        // Clear OTP error
        if (fieldErrors.otp) {
            setFieldErrors(prev => ({ ...prev, otp: '' }));
        }

        // Auto-focus next input
        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOTPKeyDown = (index, e) => {
        // Prevent form submission on Enter
        if (e.key === 'Enter') {
            e.preventDefault();
            // If all 6 digits entered, trigger verify
            if (mobileData.otp.join('').length === 6) {
                handleVerifyOTP();
            }
            return;
        }

        // Handle backspace
        if (e.key === 'Backspace' && !mobileData.otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOTPPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pastedData.length === 6) {
            const newOTP = pastedData.split('');
            setMobileData(prev => ({ ...prev, otp: newOTP }));
            otpRefs.current[5]?.focus();
        }
    };

    const handleFieldBlur = (e) => {
        const { name, value } = e.target;

        setTouchedFields(prev => ({
            ...prev,
            [name]: true
        }));

        const fieldError = validateField(name, value);
        setFieldErrors(prev => ({
            ...prev,
            [name]: fieldError
        }));
    };

    const handleSendOTP = async () => {
        if (!validateMobileForm(false)) {
            return;
        }

        setError('');
        setLoading(true);

        try {
            const response = await sendOTP(mobileData.phone, mobileData.companyDomain);
            setOtpSent(true);
            setOtpTimer(30); // 30 second cooldown for resend

            // Show demo OTP hint if available
            if (response.hint) {
                setDemoOTPHint(response.hint);
            }

            // Focus first OTP input
            setTimeout(() => {
                otpRefs.current[0]?.focus();
            }, 100);
        } catch (error) {
            console.error('Send OTP error:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to send OTP. Please try again.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (otpTimer > 0) return;

        setError('');
        setLoading(true);

        try {
            const response = await resendOTP(mobileData.phone, mobileData.companyDomain);
            setOtpTimer(30);
            setMobileData(prev => ({ ...prev, otp: ['', '', '', '', '', ''] }));

            // Show demo OTP hint if available
            if (response.hint) {
                setDemoOTPHint(response.hint);
            }

            otpRefs.current[0]?.focus();
        } catch (error) {
            console.error('Resend OTP error:', error);
            setError('Failed to resend OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        if (!validateMobileForm(true)) {
            return;
        }

        setError('');
        setLoading(true);

        try {
            const otpValue = mobileData.otp.join('');
            const response = await verifyOTP(mobileData.phone, otpValue, mobileData.companyDomain);

            // Fetch data from backend after successful login
            await fetchDataAfterLogin(response);

            // Determine redirect path based on user role
            const path = getRedirectPath(response.user);
            setRedirectPath(path);
            setShowLoader(true);
        } catch (error) {
            console.error('Verify OTP error:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Invalid OTP. Please try again.';
            setError(errorMessage);
            setFieldErrors(prev => ({ ...prev, otp: errorMessage }));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateEmailForm()) {
            return;
        }

        setError('');
        setLoading(true);

        try {
            const response = await login(formData.email, formData.password, formData.companyDomain);

            // Fetch data from backend after successful login
            await fetchDataAfterLogin(response);

            // Determine redirect path based on user role
            const path = getRedirectPath(response.user);
            setRedirectPath(path);
            setShowLoader(true);
        } catch (error) {
            console.error('Login error:', error);

            const errorMessage = error.response?.data?.error || error.message || 'Login failed. Please try again.';

            if (errorMessage.toLowerCase().includes('email') || errorMessage.toLowerCase().includes('user')) {
                setFieldErrors(prev => ({
                    ...prev,
                    email: 'Invalid email address or user not found'
                }));
                setTouchedFields(prev => ({
                    ...prev,
                    email: true
                }));
            } else if (errorMessage.toLowerCase().includes('password')) {
                setFieldErrors(prev => ({
                    ...prev,
                    password: 'Incorrect password'
                }));
                setTouchedFields(prev => ({
                    ...prev,
                    password: true
                }));
            } else if (errorMessage.toLowerCase().includes('domain') || errorMessage.toLowerCase().includes('company')) {
                setFieldErrors(prev => ({
                    ...prev,
                    companyDomain: 'Invalid company domain'
                }));
                setTouchedFields(prev => ({
                    ...prev,
                    companyDomain: true
                }));
            } else {
                setError(errorMessage);
            }

            setLoading(false);
        }
    };

    const fetchDataAfterLogin = async (response) => {
        if (fetchHolidays) {
            fetchHolidays().catch(err => console.log('Holiday fetch failed:', err));
        }
        if (fetchEmployees) {
            fetchEmployees().catch(err => console.log('Employee fetch failed:', err));
        }
        if (fetchAttendance && response.user.id) {
            fetchAttendance(response.user.id).catch(err => console.log('Attendance fetch failed:', err));
        }
        if (fetchLeaveData) {
            fetchLeaveData().catch(err => console.log('Leave fetch failed:', err));
        }
        if (fetchPayrollData && response.user.id) {
            fetchPayrollData(response.user.id).catch(err => console.log('Payroll fetch failed:', err));
        }
        if (fetchOnboardingData) {
            fetchOnboardingData().catch(err => console.log('Onboarding fetch failed:', err));
        }
        if (fetchChecklistConfig) {
            fetchChecklistConfig().catch(err => console.log('Checklist config fetch failed:', err));
        }
    };

    const getRedirectPath = (user) => {
        if (user.is_admin || user.role_name === 'HR Admin' || user.role_name === 'Super Admin' || user.access_level === 'admin') {
            return '/admin/dashboard';
        } else if (user.is_manager || user.role === 'manager' || user.role_name === 'Manager' || user.access_level === 'manager') {
            return '/manager/dashboard';
        } else {
            return '/employee/dashboard';
        }
    };

    const handleForgotPassword = async (e) => {
        e?.preventDefault();

        if (!forgotPasswordEmail || !forgotPasswordDomain) {
            setForgotPasswordError('Please enter both email and company domain');
            return;
        }

        setForgotPasswordError('');
        setForgotPasswordLoading(true);

        try {
            await authService.forgotPassword(forgotPasswordEmail, forgotPasswordDomain);
            setForgotPasswordSuccess(true);
        } catch (error) {
            setForgotPasswordError(error.response?.data?.error || 'Failed to send reset email. Please try again.');
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    const openForgotPassword = () => {
        setForgotPasswordEmail(formData.email);
        setForgotPasswordDomain(formData.companyDomain);
        setForgotPasswordSuccess(false);
        setForgotPasswordError('');
        setShowForgotPassword(true);
    };

    const closeForgotPassword = () => {
        setShowForgotPassword(false);
        setForgotPasswordSuccess(false);
        setForgotPasswordError('');
    };

    const handleLoaderComplete = () => {
        setShowLoader(false);
        if (redirectPath) {
            navigate(redirectPath);
        }
    };

    const getFieldClassName = (fieldName, source = 'email') => {
        const data = source === 'email' ? formData : mobileData;
        const baseClass = "appearance-none block w-full pl-10 pr-3 py-2 bg-gray-800 border rounded-md shadow-sm placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-colors duration-200";

        if (touchedFields[fieldName] && fieldErrors[fieldName]) {
            return `${baseClass} border-red-500 text-red-200 placeholder-red-400 focus:ring-red-500 focus:border-red-500`;
        } else if (touchedFields[fieldName] && !fieldErrors[fieldName] && data[fieldName]) {
            return `${baseClass} border-green-500 focus:ring-green-500 focus:border-green-500`;
        } else {
            return `${baseClass} border-gray-700 focus:border-yellow-600`;
        }
    };

    const getFieldIcon = (fieldName, source = 'email') => {
        const data = source === 'email' ? formData : mobileData;

        if (touchedFields[fieldName] && fieldErrors[fieldName]) {
            return <AlertCircle className="h-5 w-5 text-red-500" />;
        } else if (touchedFields[fieldName] && !fieldErrors[fieldName] && data[fieldName]) {
            return <CheckCircle className="h-5 w-5 text-green-500" />;
        }

        switch (fieldName) {
            case 'companyDomain':
                return <Building className="h-5 w-5 text-yellow-600" />;
            case 'email':
                return <Mail className="h-5 w-5 text-yellow-600" />;
            case 'password':
                return <Lock className="h-5 w-5 text-yellow-600" />;
            case 'phone':
                return <Phone className="h-5 w-5 text-yellow-600" />;
            default:
                return null;
        }
    };

    const handleSwitchMethod = (method) => {
        setLoginMethod(method);
        setError('');
        setFieldErrors({
            companyDomain: '',
            email: '',
            password: '',
            phone: '',
            otp: ''
        });
        setTouchedFields({
            companyDomain: false,
            email: false,
            password: false,
            phone: false
        });
        setOtpSent(false);
        setOtpTimer(0);
        setDemoOTPHint('');
        setMobileData(prev => ({ ...prev, otp: ['', '', '', '', '', ''] }));
    };

    return (
        <>
            {showLoader && <OakTreeLoader onComplete={handleLoaderComplete} />}
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex">
                {/* Left: Feature Showcase */}
                <div className="hidden md:flex w-[55%]">
                    <FeatureShowcase />
                </div>
                {/* Right: Logo + Login Form */}
                <div className="w-full md:w-[45%] flex items-center justify-center p-4 md:p-8 overflow-y-auto">
                <div className="max-w-md w-full space-y-8">
                    {/* Logo/Header */}
                    <div className="text-center">
                        <div className="mx-auto flex flex-col items-center justify-center mb-6 gap-0">
                            <div className="relative overflow-hidden h-48 w-full max-w-sm">
                                <video
                                    src={logoVideo}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="absolute top-0 left-1/2 -translate-x-1/2 h-56 w-auto"
                                    style={{ mixBlendMode: 'lighten' }}
                                />
                            </div>
                            <p className="text-[5px] text-yellow-600 leading-none ml-40 -mt-12">
                                Powered by KONAV Technologies Pvt. Ltd.
                            </p>
                        </div>
                    </div>

                    {/* Login Form */}
                    <div className="bg-gradient-to-br from-gray-900 to-black shadow-2xl rounded-lg p-8 border border-yellow-600/30">
                        {/* Login Method Tabs */}
                        <div className="flex mb-6 bg-gray-800 rounded-lg p-1">
                            <button
                                type="button"
                                onClick={() => handleSwitchMethod('email')}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                                    loginMethod === 'email'
                                        ? 'bg-yellow-600 text-black'
                                        : 'text-gray-400 hover:text-white'
                                }`}
                            >
                                <Mail className="h-4 w-4" />
                                Email
                            </button>
                            <button
                                type="button"
                                onClick={() => handleSwitchMethod('mobile')}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                                    loginMethod === 'mobile'
                                        ? 'bg-yellow-600 text-black'
                                        : 'text-gray-400 hover:text-white'
                                }`}
                            >
                                <Smartphone className="h-4 w-4" />
                                Mobile OTP
                            </button>
                        </div>

                        {error && (
                            <Alert type="error" message={error} onClose={() => setError('')} />
                        )}

                        {/* Demo OTP Hint */}
                        {demoOTPHint && loginMethod === 'mobile' && otpSent && (
                            <div className="mb-4 p-3 bg-blue-900/50 border border-blue-500/50 rounded-md">
                                <p className="text-sm text-blue-300 flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4" />
                                    {demoOTPHint}
                                </p>
                            </div>
                        )}

                        {/* Email Login Form */}
                        {loginMethod === 'email' && (
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                {/* Company Domain */}
                                <div>
                                    <label htmlFor="companyDomain" className="block text-sm font-medium text-yellow-500">
                                        Company Domain
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            {getFieldIcon('companyDomain')}
                                        </div>
                                        <input
                                            id="companyDomain"
                                            name="companyDomain"
                                            type="text"
                                            required
                                            value={formData.companyDomain}
                                            onChange={handleFieldChange}
                                            onBlur={handleFieldBlur}
                                            className={getFieldClassName('companyDomain')}
                                            placeholder="yourcompany"
                                            aria-invalid={touchedFields.companyDomain && !!fieldErrors.companyDomain}
                                            aria-describedby={fieldErrors.companyDomain ? 'companyDomain-error' : undefined}
                                        />
                                        {touchedFields.companyDomain && fieldErrors.companyDomain && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                <AlertCircle className="h-5 w-5 text-red-500" />
                                            </div>
                                        )}
                                    </div>
                                    {touchedFields.companyDomain && fieldErrors.companyDomain && (
                                        <p id="companyDomain-error" className="mt-1 text-sm text-red-500 flex items-center gap-1">
                                            <AlertCircle className="h-4 w-4" />
                                            {fieldErrors.companyDomain}
                                        </p>
                                    )}
                                </div>

                                {/* Email */}
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-yellow-500">
                                        Email Address
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            {getFieldIcon('email')}
                                        </div>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            value={formData.email}
                                            onChange={handleFieldChange}
                                            onBlur={handleFieldBlur}
                                            className={getFieldClassName('email')}
                                            placeholder="john@example.com"
                                            aria-invalid={touchedFields.email && !!fieldErrors.email}
                                            aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                                        />
                                        {touchedFields.email && fieldErrors.email && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                <AlertCircle className="h-5 w-5 text-red-500" />
                                            </div>
                                        )}
                                    </div>
                                    {touchedFields.email && fieldErrors.email && (
                                        <p id="email-error" className="mt-1 text-sm text-red-500 flex items-center gap-1">
                                            <AlertCircle className="h-4 w-4" />
                                            {fieldErrors.email}
                                        </p>
                                    )}
                                </div>

                                {/* Password */}
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-yellow-500">
                                        Password
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            {getFieldIcon('password')}
                                        </div>
                                        <input
                                            id="password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            autoComplete="current-password"
                                            required
                                            value={formData.password}
                                            onChange={handleFieldChange}
                                            onBlur={handleFieldBlur}
                                            className={getFieldClassName('password')}
                                            placeholder="••••••••"
                                            aria-invalid={touchedFields.password && !!fieldErrors.password}
                                            aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-10 flex items-center"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-5 w-5 text-yellow-600" />
                                            ) : (
                                                <Eye className="h-5 w-5 text-yellow-600" />
                                            )}
                                        </button>
                                        {touchedFields.password && fieldErrors.password && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                <AlertCircle className="h-5 w-5 text-red-500" />
                                            </div>
                                        )}
                                    </div>
                                    {touchedFields.password && fieldErrors.password && (
                                        <p id="password-error" className="mt-1 text-sm text-red-500 flex items-center gap-1">
                                            <AlertCircle className="h-4 w-4" />
                                            {fieldErrors.password}
                                        </p>
                                    )}
                                </div>

                                {/* Remember me & Forgot password */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <input
                                            id="remember-me"
                                            name="remember-me"
                                            type="checkbox"
                                            className="h-4 w-4 text-yellow-600 focus:ring-yellow-600 bg-gray-800 border-gray-700 rounded"
                                        />
                                        <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300">
                                            Remember me
                                        </label>
                                    </div>

                                    <div className="text-sm">
                                        <button
                                            type="button"
                                            onClick={openForgotPassword}
                                            className="font-medium text-yellow-500 hover:text-yellow-400"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                    >
                                        {loading ? 'Signing in...' : 'Sign in'}
                                    </button>
                                </div>

                                {/* New to OakHR Link */}
                                <div className="text-center mt-3 text-xs text-gray-400">
                                    <span className="mr-1">New to OakHR?</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowOrgModal(true);
                                            setOrgStep('form');
                                        }}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-yellow-500 hover:text-yellow-400 hover:bg-yellow-900/20 font-medium transition-colors"
                                    >
                                        <Building className="h-3 w-3" />
                                        Create organization
                                    </button>
                                </div>

                                {/* Terms & Privacy Agreement */}
                                <div className="mt-4 text-xs text-gray-400">
                                    By accessing or logging in to OakHR, you agree to our{' '}
                                    <a href="/admin/privacy" target="_blank" rel="noopener noreferrer" className="text-yellow-500 hover:text-yellow-400 underline">
                                        Privacy Policy
                                    </a>
                                    {' '}and{' '}
                                    <a href="/admin/terms" target="_blank" rel="noopener noreferrer" className="text-yellow-500 hover:text-yellow-400 underline">
                                        Terms & Conditions
                                    </a>
                                </div>
                            </form>
                        )}

                        {/* Mobile OTP Login Form */}
                        {loginMethod === 'mobile' && (
                            <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
                                {/* Company Domain */}
                                <div>
                                    <label htmlFor="mobile-companyDomain" className="block text-sm font-medium text-yellow-500">
                                        Company Domain
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            {getFieldIcon('companyDomain', 'mobile')}
                                        </div>
                                        <input
                                            id="mobile-companyDomain"
                                            name="companyDomain"
                                            type="text"
                                            required
                                            value={mobileData.companyDomain}
                                            onChange={handleMobileFieldChange}
                                            onBlur={handleFieldBlur}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    document.getElementById('phone')?.focus();
                                                }
                                            }}
                                            disabled={otpSent}
                                            className={`${getFieldClassName('companyDomain', 'mobile')} ${otpSent ? 'opacity-60 cursor-not-allowed' : ''}`}
                                            placeholder="yourcompany"
                                        />
                                    </div>
                                    {touchedFields.companyDomain && fieldErrors.companyDomain && (
                                        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                                            <AlertCircle className="h-4 w-4" />
                                            {fieldErrors.companyDomain}
                                        </p>
                                    )}
                                </div>

                                {/* Phone Number */}
                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium text-yellow-500">
                                        Mobile Number
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            {getFieldIcon('phone', 'mobile')}
                                        </div>
                                        <div className="absolute inset-y-0 left-10 flex items-center pointer-events-none">
                                            <span className="text-gray-400 text-sm">+91</span>
                                        </div>
                                        <input
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            required
                                            value={mobileData.phone}
                                            onChange={handleMobileFieldChange}
                                            onBlur={handleFieldBlur}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    if (!otpSent) handleSendOTP();
                                                }
                                            }}
                                            disabled={otpSent}
                                            maxLength="10"
                                            className={`${getFieldClassName('phone', 'mobile')} pl-20 ${otpSent ? 'opacity-60 cursor-not-allowed' : ''}`}
                                            placeholder="9876543210"
                                        />
                                    </div>
                                    {touchedFields.phone && fieldErrors.phone && (
                                        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                                            <AlertCircle className="h-4 w-4" />
                                            {fieldErrors.phone}
                                        </p>
                                    )}
                                </div>

                                {/* OTP Input */}
                                {otpSent && (
                                    <div>
                                        <label className="block text-sm font-medium text-yellow-500 mb-2">
                                            Enter OTP
                                        </label>
                                        <div className="flex gap-2 justify-center">
                                            {mobileData.otp.map((digit, index) => (
                                                <input
                                                    key={index}
                                                    ref={el => otpRefs.current[index] = el}
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength="1"
                                                    value={digit}
                                                    onChange={(e) => handleOTPChange(index, e.target.value)}
                                                    onKeyDown={(e) => handleOTPKeyDown(index, e)}
                                                    onPaste={index === 0 ? handleOTPPaste : undefined}
                                                    className={`w-12 h-12 text-center text-xl font-bold bg-gray-800 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-colors ${
                                                        fieldErrors.otp ? 'border-red-500' : 'border-gray-700'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                        {fieldErrors.otp && (
                                            <p className="mt-2 text-sm text-red-500 flex items-center justify-center gap-1">
                                                <AlertCircle className="h-4 w-4" />
                                                {fieldErrors.otp}
                                            </p>
                                        )}

                                        {/* Resend OTP */}
                                        <div className="mt-3 text-center">
                                            {otpTimer > 0 ? (
                                                <p className="text-sm text-gray-400">
                                                    Resend OTP in <span className="text-yellow-500 font-medium">{otpTimer}s</span>
                                                </p>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={handleResendOTP}
                                                    disabled={loading}
                                                    className="text-sm text-yellow-500 hover:text-yellow-400 font-medium"
                                                >
                                                    Resend OTP
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="space-y-3">
                                    {!otpSent ? (
                                        <button
                                            type="button"
                                            onClick={handleSendOTP}
                                            disabled={loading}
                                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                        >
                                            {loading ? 'Sending OTP...' : 'Send OTP'}
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={handleVerifyOTP}
                                                disabled={loading || mobileData.otp.join('').length !== 6}
                                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                            >
                                                {loading ? 'Verifying...' : 'Verify & Sign in'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setOtpSent(false);
                                                    setOtpTimer(0);
                                                    setDemoOTPHint('');
                                                    setMobileData(prev => ({ ...prev, otp: ['', '', '', '', '', ''] }));
                                                    setFieldErrors(prev => ({ ...prev, otp: '' }));
                                                }}
                                                className="w-full flex justify-center py-2 px-4 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-300 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 focus:ring-offset-gray-900 transition-all duration-200"
                                            >
                                                Change Number
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* reCAPTCHA container for Firebase Phone Auth - required for invisible reCAPTCHA */}
                                <div id="recaptcha-container"></div>
                            </form>
                        )}
                    </div>
                </div>
                </div>
            </div>

            {/* Create Organization Modal */}
            {showOrgModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-gray-900 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 border border-yellow-600/40">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-2">
                                <Building className="w-5 h-5 text-yellow-500" />
                                <h2 className="text-xl font-bold text-white">Create a new organization</h2>
                            </div>
                            <button
                                onClick={() => {
                                    setShowOrgModal(false);
                                    setOrgStep('form');
                                    // Reset all states
                                    setOrgForm({
                                        companyName: '',
                                        domain: '',
                                        workEmail: '',
                                        emailVerified: false,
                                        phone: '',
                                        phoneVerified: false,
                                        firstName: '',
                                        middleName: '',
                                        lastName: '',
                                        teamSize: '',
                                        password: '',
                                        confirmPassword: ''
                                    });
                                    setEmailOTP(['', '', '', '', '', '']);
                                    setPhoneOTP(['', '', '', '', '', '']);
                                    setEmailOTPSent(false);
                                    setPhoneOTPSent(false);
                                    setEmailOTPTimer(0);
                                    setPhoneOTPTimer(0);
                                    setEmailDuplicate(null);
                                    setPhoneDuplicate(null);
                                    setError('');
                                }}
                                className="text-gray-400 hover:text-white"
                            >
                                <XIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {orgStep === 'form' && (
                            <form className="space-y-5" onSubmit={handleOrgSubmit}>
                                {/* Personal Information Section */}
                                <div className="border-b border-gray-700 pb-4">
                                    <h3 className="text-sm font-semibold text-yellow-500 mb-3 flex items-center gap-2">
                                        <User className="w-4 h-4" />
                                        Personal Information
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                                First Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={orgForm.firstName}
                                                onChange={(e) => handleOrgFieldChange('firstName', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                                placeholder="John"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-1">Middle Name</label>
                                            <input
                                                type="text"
                                                value={orgForm.middleName}
                                                onChange={(e) => handleOrgFieldChange('middleName', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                                placeholder="optional"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                                Last Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={orgForm.lastName}
                                                onChange={(e) => handleOrgFieldChange('lastName', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                                placeholder="Doe"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Organization Details Section */}
                                <div className="border-b border-gray-700 pb-4">
                                    <h3 className="text-sm font-semibold text-yellow-500 mb-3 flex items-center gap-2">
                                        <Building className="w-4 h-4" />
                                        Organization Details
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                                Company Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={orgForm.companyName}
                                                onChange={(e) => handleOrgFieldChange('companyName', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                                placeholder="Acme Corp"
                                                required
                                            />
                                        </div>

                                        {/* Organization Domain */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                                Organization Domain <span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex items-center">
                                                <span className="text-gray-400 text-sm mr-2">portal.oak-hr.com/</span>
                                                <input
                                                    type="text"
                                                    value={orgForm.domain || ''}
                                                    onChange={(e) => {
                                                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
                                                        handleOrgFieldChange('domain', value);
                                                    }}
                                                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                                    placeholder="checkcorp"
                                                    required
                                                />
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">Unique identifier for your organization (e.g., checkcorp)</p>
                                            {orgDomainError && (
                                                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" />
                                                    {orgDomainError}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-1">Team Size</label>
                                            <select
                                                value={orgForm.teamSize}
                                                onChange={(e) => handleOrgFieldChange('teamSize', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                            >
                                                <option value="">Select team size</option>
                                                <option value="1-10">1-10 employees</option>
                                                <option value="11-50">11-50 employees</option>
                                                <option value="51-200">51-200 employees</option>
                                                <option value="201-500">201-500 employees</option>
                                                <option value="500+">500+ employees</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Verification Section */}
                                <div>
                                    <h3 className="text-sm font-semibold text-yellow-500 mb-3 flex items-center gap-2">
                                        <AtSign className="w-4 h-4" />
                                        Contact Verification
                                    </h3>

                                    <div className="space-y-4">
                                        {/* Email Verification */}
                                        <div className={`p-4 rounded-lg border ${emailDuplicate ? 'border-red-500 bg-red-900/10' : orgForm.emailVerified ? 'border-green-500 bg-green-900/10' : 'border-gray-700'}`}>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Email Address <span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex gap-2">
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="email"
                                                        value={orgForm.workEmail}
                                                        onChange={(e) => handleOrgFieldChange('workEmail', e.target.value)}
                                                        onBlur={(e) => e.target.value && checkEmailDuplicate(e.target.value)}
                                                        disabled={orgForm.emailVerified}
                                                        className={`w-full px-3 py-2 bg-gray-800 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 pr-10 ${
                                                            emailDuplicate ? 'border-red-500' : orgForm.emailVerified ? 'border-green-500' : 'border-gray-700'
                                                        }`}
                                                        placeholder="you@company.com"
                                                        required
                                                    />
                                                    {orgForm.emailVerified && (
                                                        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                                                    )}
                                                </div>
                                                {!orgForm.emailVerified && (
                                                    <button
                                                        type="button"
                                                        onClick={handleSendEmailOTP}
                                                        disabled={emailVerifying || !orgForm.workEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orgForm.workEmail)}
                                                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                                    >
                                                        {emailVerifying ? 'Sending...' : emailOTPSent ? 'Resend OTP' : 'Verify'}
                                                    </button>
                                                )}
                                            </div>

                                            {/* Email OTP Input */}
                                            {emailOTPSent && !orgForm.emailVerified && (
                                                <div className="mt-3">
                                                    <div className="flex gap-2 justify-center mb-2">
                                                        {emailOTP.map((digit, index) => (
                                                            <input
                                                                key={index}
                                                                ref={el => emailOTPRefs.current[index] = el}
                                                                type="text"
                                                                inputMode="numeric"
                                                                maxLength={1}
                                                                value={digit}
                                                                onChange={(e) => handleEmailOTPChange(index, e.target.value)}
                                                                className="w-10 h-10 text-center text-lg font-bold bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                                            />
                                                        ))}
                                                    </div>
                                                    <div className="flex items-center justify-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={handleVerifyEmailOTP}
                                                            disabled={emailVerifying || emailOTP.join('').length !== 6}
                                                            className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            {emailVerifying ? 'Verifying...' : 'Verify Email'}
                                                        </button>
                                                        {emailOTPTimer > 0 && (
                                                            <span className="text-xs text-gray-400">
                                                                Resend in {emailOTPTimer}s
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Email Error Message */}
                                            {emailDuplicate && (
                                                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                                                    <AlertCircle className="w-4 h-4" />
                                                    {emailDuplicate}
                                                </p>
                                            )}
                                            {orgForm.emailVerified && (
                                                <p className="mt-2 text-sm text-green-500 flex items-center gap-1">
                                                    <CheckCircle className="w-4 h-4" />
                                                    Email verified successfully
                                                </p>
                                            )}
                                        </div>

                                        {/* Phone Number - No Verification Required */}
                                        <div className="p-4 rounded-lg border border-gray-700">
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Mobile Number <span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex gap-2">
                                                <div className="flex-1 relative">
                                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                                        <span className="text-gray-400 text-sm">+91</span>
                                                    </div>
                                                    <input
                                                        type="tel"
                                                        value={orgForm.phone}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                            handleOrgFieldChange('phone', value);
                                                        }}
                                                        className="w-full pl-12 pr-10 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                                        placeholder="9876543210"
                                                        maxLength={10}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">For future communication and account recovery</p>
                                        </div>
                                    </div>
                                </div>

                                {/* General Error Message */}
                                {error && (
                                    <div className="p-3 bg-red-900/30 border border-red-700 rounded-md flex items-start">
                                        <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                                        <p className="text-red-400 text-sm">{error}</p>
                                    </div>
                                )}

                                <p className="text-xs text-gray-400">
                                    <span className="text-yellow-500 font-medium">Note:</span> We'll set up your OakHR workspace and send you the login credentials after verification.
                                </p>

                                <div className="flex justify-end gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowOrgModal(false);
                                            setOrgStep('form');
                                            // Reset all states
                                            setOrgForm({
                                                companyName: '',
                                                workEmail: '',
                                                emailVerified: false,
                                                phone: '',
                                                phoneVerified: false,
                                                firstName: '',
                                                middleName: '',
                                                lastName: '',
                                                teamSize: '',
                                                password: '',
                                                confirmPassword: ''
                                            });
                                            setEmailOTP(['', '', '', '', '', '']);
                                            setPhoneOTP(['', '', '', '', '', '']);
                                            setEmailOTPSent(false);
                                            setPhoneOTPSent(false);
                                            setEmailOTPTimer(0);
                                            setPhoneOTPTimer(0);
                                            setEmailDuplicate(null);
                                            setPhoneDuplicate(null);
                                            setError('');
                                        }}
                                        className="px-4 py-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!orgForm.emailVerified}
                                        className="px-4 py-2 rounded-md bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-semibold hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Create Organization
                                    </button>
                                </div>
                            </form>
                        )}

                        {orgStep === 'password' && (
                            <form className="space-y-5" onSubmit={handlePasswordSetup}>
                                <div className="text-center mb-6">
                                    <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-yellow-900 bg-opacity-30 mb-4">
                                        <Lock className="w-8 h-8 text-yellow-500" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-white">Set Your Password</h3>
                                    <p className="text-gray-400 text-sm mt-2">
                                        Almost done! Create a password for your admin account.
                                    </p>
                                </div>

                                {/* Account Summary */}
                                <div className="bg-gray-800 rounded-lg p-4 space-y-2 border border-gray-700">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center text-black font-semibold">
                                            {orgForm.firstName?.[0] || 'U'}{orgForm.lastName?.[0] || 'N'}
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">
                                                {orgForm.firstName} {orgForm.middleName} {orgForm.lastName}
                                            </p>
                                            <p className="text-gray-400 text-sm">{orgForm.workEmail}</p>
                                        </div>
                                    </div>
                                    <div className="pt-2 border-t border-gray-700">
                                        <p className="text-xs text-gray-400">
                                            Organization: <span className="text-yellow-500">{orgForm.companyName}</span>
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            Role: <span className="text-green-500">Admin</span> (with Employee access)
                                        </p>
                                    </div>
                                </div>

                                {/* Password Fields */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Password <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={orgForm.password}
                                                onChange={(e) => handleOrgFieldChange('password', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 pr-10"
                                                placeholder="Create a strong password"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                                            >
                                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Confirm Password <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={orgForm.confirmPassword}
                                                onChange={(e) => handleOrgFieldChange('confirmPassword', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 pr-10"
                                                placeholder="Confirm your password"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                                            >
                                                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        </div>
                                        {orgForm.confirmPassword && orgForm.password !== orgForm.confirmPassword && (
                                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                                <AlertCircle className="w-3 h-3" />
                                                Passwords do not match
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Error Message */}
                                {error && (
                                    <div className="p-3 bg-red-900/30 border border-red-700 rounded-md flex items-start">
                                        <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                                        <p className="text-red-400 text-sm">{error}</p>
                                    </div>
                                )}

                                {/* Success Message */}
                                {!error && orgForm.password && orgForm.confirmPassword && orgForm.password === orgForm.confirmPassword && orgForm.password.length >= 6 && (
                                    <div className="p-3 bg-green-900/30 border border-green-700 rounded-md flex items-start">
                                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                        <div className="text-green-400 text-sm">
                                            <p className="font-medium">Password ready!</p>
                                            <p className="text-xs mt-1">Click "Create Account" to complete your setup</p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-between items-center pt-2">
                                    <button
                                        type="button"
                                        onClick={handleBackToForm}
                                        className="px-4 py-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!orgForm.password || !orgForm.confirmPassword || orgForm.password !== orgForm.confirmPassword || orgForm.password.length < 6}
                                        className="px-6 py-2 rounded-md bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-semibold hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Create Account
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-gray-900 rounded-lg shadow-xl max-w-md w-full p-6 border border-gray-700">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-white">Reset Password</h2>
                            <button
                                onClick={closeForgotPassword}
                                className="text-gray-400 hover:text-white"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {!forgotPasswordSuccess ? (
                            <>
                                <p className="text-gray-400 text-sm mb-4">
                                    Enter your email and company domain to receive a password reset link.
                                </p>

                                {forgotPasswordError && (
                                    <div className="mb-4 p-3 bg-red-900 bg-opacity-30 border border-red-700 rounded-md flex items-start">
                                        <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                                        <p className="text-red-400 text-sm">{forgotPasswordError}</p>
                                    </div>
                                )}

                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-300 mb-1">
                                            Email Address
                                        </label>
                                        <input
                                            id="forgot-email"
                                            type="email"
                                            value={forgotPasswordEmail}
                                            onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                            placeholder="you@example.com"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="forgot-domain" className="block text-sm font-medium text-gray-300 mb-1">
                                            Company Domain
                                        </label>
                                        <input
                                            id="forgot-domain"
                                            type="text"
                                            value={forgotPasswordDomain}
                                            onChange={(e) => setForgotPasswordDomain(e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                            placeholder="your-company"
                                        />
                                    </div>

                                    <button
                                        onClick={handleForgotPassword}
                                        disabled={forgotPasswordLoading}
                                        className="w-full py-2 px-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    >
                                        {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="text-center">
                                <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-900 bg-opacity-30 mb-4">
                                    <CheckCircle className="w-6 h-6 text-green-500" />
                                </div>
                                <h3 className="text-lg font-semibold text-white mb-2">Check Your Email</h3>
                                <p className="text-gray-400 text-sm mb-4">
                                    We've sent a password reset link to <span className="text-yellow-500">{forgotPasswordEmail}</span>.
                                    Please check your inbox and spam folder.
                                </p>
                                <button
                                    onClick={closeForgotPassword}
                                    className="py-2 px-4 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-md transition-all"
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Login;
